import { z } from "zod";
import {
	BOARD_ROLES,
	MESSAGE_TYPES,
	ANIMATION_TYPES,
	MIN_SLIDE_DURATION_MS,
	MAX_SLIDE_DURATION_MS,
	DEFAULT_MESSAGE_DURATION_MS,
	DEFAULT_IMAGE_DURATION_MS,
	DEFAULT_WEATHER_DURATION_MS,
	WEATHER_LAYOUTS,
	WEATHER_STYLES,
} from "./constants";

// ─── Rôles ──────────────────────────────────────────────────────

export type BoardRole = (typeof BOARD_ROLES)[number];

// ─── Auth ────────────────────────────────────────────────────────

export const loginSchema = z.object({
	email: z.string().email(),
	password: z.string().min(8),
});

export const registerSchema = z.object({
	email: z.string().email(),
	password: z.string().min(8),
	passwordConfirm: z.string().min(8),
	name: z.string().min(2),
	newsletterOptIn: z.boolean().optional().default(false),
}).refine((data) => data.password === data.passwordConfirm, {
	message: "Passwords must match",
	path: ["passwordConfirm"],
});

// ─── Boards ──────────────────────────────────────────────────────

export const createBoardSchema = z.object({
	name: z.string().min(2).max(100),
	slug: z.string().min(2).max(50)
		.regex(/^[a-z0-9-]+$/, "Slug must be lowercase alphanumeric with hyphens"),
	description: z.string().max(500).optional().default(""),
	public: z.boolean().optional().default(false),
	settings: z.object({
		slideInterval: z.number().min(3).max(300).optional().default(10),
		theme: z.enum(["light", "dark", "warm"]).optional().default("warm"),
		nightModeStart: z.string().optional().default("22:00"),
		nightModeEnd: z.string().optional().default("07:00"),
		timezone: z.string().optional().default("Europe/Paris"),
		language: z.enum(["fr", "en", "nl"]).optional().default("fr"),
		messageDurationMs: z.number().int()
			.min(MIN_SLIDE_DURATION_MS).max(MAX_SLIDE_DURATION_MS)
			.optional().default(DEFAULT_MESSAGE_DURATION_MS),
		imageDurationMs: z.number().int()
			.min(MIN_SLIDE_DURATION_MS).max(MAX_SLIDE_DURATION_MS)
			.optional().default(DEFAULT_IMAGE_DURATION_MS),
		weatherDurationMs: z.number().int()
			.min(MIN_SLIDE_DURATION_MS).max(MAX_SLIDE_DURATION_MS)
			.optional().default(DEFAULT_WEATHER_DURATION_MS),
	}).optional().default({}),
});

export const updateBoardSchema = createBoardSchema.partial();

export const boardSchema = createBoardSchema.extend({
	id: z.string(),
	created: z.string(),
	updated: z.string(),
	member_count: z.number().optional(),
});

// ─── Board Members ───────────────────────────────────────────────

export const addMemberSchema = z.object({
	email: z.string().email(),
	role: z.enum(BOARD_ROLES),
});

export const memberSchema = z.object({
	id: z.string(),
	board_id: z.string(),
	user_id: z.string(),
	email: z.string().email(),
	name: z.string(),
	role: z.enum(BOARD_ROLES),
	created: z.string(),
});

// ─── Photos ──────────────────────────────────────────────────────

export const createPhotoSchema = z.object({
	caption: z.string().max(500).optional().default(""),
});

export const photoSchema = createPhotoSchema.extend({
	id: z.string(),
	board_id: z.string(),
	image_url: z.string(),
	thumbnail_url: z.string().optional(),
	created: z.string(),
	updated: z.string(),
});

// ─── Messages ────────────────────────────────────────────────────

export const createMessageSchema = z.object({
	text: z.string().min(1).max(1000),
	type: z.enum(MESSAGE_TYPES).optional().default("custom"),
	scheduled_at: z.string().datetime().optional(),
});

export const messageSchema = createMessageSchema.extend({
	id: z.string(),
	board_id: z.string(),
	author_id: z.string(),
	author_name: z.string(),
	created: z.string(),
	updated: z.string(),
});

// ─── Animations ──────────────────────────────────────────────────

export const createAnimationSchema = z.object({
	type: z.enum(ANIMATION_TYPES),
	config: z.record(z.string(), z.unknown()).optional().default({}),
	duration: z.number().min(1).max(300).optional().default(30),
	scheduled_at: z.string().datetime().optional(),
});

export const animationSchema = createAnimationSchema.extend({
	id: z.string(),
	board_id: z.string(),
	created: z.string(),
	updated: z.string(),
});

// ─── Display Feed ────────────────────────────────────────────────

export const feedItemSchema = z.object({
	id: z.string(),
	type: z.enum(["photo", "message", "animation", "weather"]),
	board_id: z.string(),
	data: z.record(z.string(), z.unknown()),
	created: z.string(),
});

// ─── Weather ─────────────────────────────────────────────────────

export const weatherLayoutSchema = z.enum(WEATHER_LAYOUTS);
export const weatherStyleSchema = z.enum(WEATHER_STYLES);

export const weatherSnapshotSchema = z.object({
	fetched_at: z.string(),
	stale: z.boolean().optional(),
	timezone: z.string(),
	current: z.object({
		temperature_c: z.number(),
		apparent_c: z.number(),
		wmo_code: z.number().int(),
		wind_kmh: z.number(),
		humidity_pct: z.number().int().min(0).max(100),
		is_day: z.boolean(),
	}),
	today: z.object({
		min_c: z.number(),
		max_c: z.number(),
		wmo_code: z.number().int(),
		sunrise: z.string(),
		sunset: z.string(),
	}).optional(),
	forecast: z.array(z.object({
		date: z.string(),
		min_c: z.number(),
		max_c: z.number(),
		wmo_code: z.number().int(),
	})).optional(),
});

export const createWeatherCardSchema = z.object({
	city_query: z.string().min(1).max(200),
	city_label: z.string().min(1).max(200),
	country_code: z.string().length(2).optional(),
	latitude: z.number().min(-90).max(90),
	longitude: z.number().min(-180).max(180),
	timezone: z.string().min(1),
	layout: weatherLayoutSchema,
	style: weatherStyleSchema.optional().default("warm"),
	display_duration_ms: z.number().int()
		.min(MIN_SLIDE_DURATION_MS).max(MAX_SLIDE_DURATION_MS)
		.optional(),
});

export const weatherCardSchema = createWeatherCardSchema.extend({
	id: z.string(),
	board_id: z.string(),
	created: z.string(),
	updated: z.string(),
	promoted_at: z.string().optional(),
});

export const geocodeResultSchema = z.object({
	name: z.string(),
	country: z.string().optional(),
	country_code: z.string().optional(),
	admin1: z.string().optional(),
	latitude: z.number(),
	longitude: z.number(),
	timezone: z.string(),
});

export const feedSchema = z.object({
	board: z.object({
		name: z.string(),
		slug: z.string(),
		settings: z.record(z.string(), z.unknown()),
	}),
	items: z.array(feedItemSchema),
	heartbeat_interval: z.number(),
});

// ─── API Responses ───────────────────────────────────────────────

export const apiResponseSchema = z.object({
	ok: z.boolean(),
	message: z.string().optional(),
});

// ─── Legacy boilerplate schemas (kept for future monetization) ───

export const newsletterSubscriptionSchema = z.object({
	email: z.string().email(),
	subscribed: z.boolean().default(true),
});

export const newsletterSendSchema = z.object({
	subject: z.string().min(3),
	html: z.string().min(10),
});

export const activityLogSchema = z.object({
	action: z.enum(["login", "logout", "dashboard_visit", "subscription_change", "feature_usage"]),
	metadata: z.record(z.string(), z.unknown()).default({}),
});

export const checkoutSchema = z.object({
	customerId: z.string().min(1),
	priceId: z.string().min(1).optional(),
});

export const aiChatSchema = z.object({ prompt: z.string().min(1), model: z.string().optional() });
export const aiSummarySchema = z.object({ content: z.string().min(1), model: z.string().optional() });
export const aiImageSchema = z.object({ prompt: z.string().min(1) });
export const aiTtsSchema = z.object({ input: z.string().min(1) });
export const aiSttSchema = z.object({ filePath: z.string().min(1) });
export const aiOcrSchema = z.object({ imagePath: z.string().min(1) });

// ─── Type Exports ────────────────────────────────────────────────

export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type CreateBoardInput = z.infer<typeof createBoardSchema>;
export type UpdateBoardInput = z.infer<typeof updateBoardSchema>;
export type Board = z.infer<typeof boardSchema>;
export type BoardSettings = NonNullable<Board["settings"]>;
export type LanguageCode = NonNullable<BoardSettings["language"]>;
export type AddMemberInput = z.infer<typeof addMemberSchema>;
export type Member = z.infer<typeof memberSchema>;
export type CreatePhotoInput = z.infer<typeof createPhotoSchema>;
export type Photo = z.infer<typeof photoSchema>;
export type CreateMessageInput = z.infer<typeof createMessageSchema>;
export type Message = z.infer<typeof messageSchema>;
export type CreateAnimationInput = z.infer<typeof createAnimationSchema>;
export type Animation = z.infer<typeof animationSchema>;
export type FeedItem = z.infer<typeof feedItemSchema>;
export type Feed = z.infer<typeof feedSchema>;
export type WeatherLayout = z.infer<typeof weatherLayoutSchema>;
export type WeatherStyle = z.infer<typeof weatherStyleSchema>;
export type WeatherSnapshot = z.infer<typeof weatherSnapshotSchema>;
export type CreateWeatherCardInput = z.infer<typeof createWeatherCardSchema>;
export type WeatherCard = z.infer<typeof weatherCardSchema>;
export type GeocodeResult = z.infer<typeof geocodeResultSchema>;
